import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import proj4 from "proj4";
import { transform } from "ol/proj";

import { authHeader } from '@/lib/api/authHeader';

import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Tooltip, useTheme } from '@mui/material';

import { ObjectAnalysisMinimap } from '@/views/Operations/ObjectAnalysisMiniMap'
import AppPage from '@/ui/AppPage/AppPage';
import api from '@/lib/api';
import BaseLayer from 'ol/layer/Base';
const apiPath = process.env.REACT_APP_APIPATH; 
const LINEPOINTCOLOR = Cesium.Color.RED;
const gs_url = process.env.REACT_APP_GEOSERVERPATH;
let X, Y;

function CesiumPage() {
	const { t } = useTranslation();
	
	const [baseLayers, setBaseLayers] = useState(null);
	const [layers, setLayers] = useState(null);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [drawAllowed, setDrawAllowed] = useState(false);
	const [drawActive, setDrawActive] = useState(false);
	const [refresh, setRefreshToken]=useState(false);
	const [handler, setHandler]=useState(null)
	const [sourceCount, setSourceCount] = useState(1);
	
	const theme = useTheme();
	
	const tileset_list = useRef();
	const tileset = useRef();
	
	const viewer = useRef();
	const camera = useRef();
	const scene = useRef();
	const globe = useRef(new Cesium.Globe(Cesium.Ellipsoid.WGS84));
	const ellipsoid = useRef(Cesium.Ellipsoid.WGS84);
	const geodesic = useRef(new Cesium.EllipsoidGeodesic());
	
	const points = useRef();
	const point1 = useRef();
	const point2 = useRef();
	const point1GeoPosition = useRef();
	const point2GeoPosition = useRef();
	const point3GeoPosition = useRef();
	
	const polylines = useRef();
	const polyline1 = useRef();
	const polyline2 = useRef();
	const polyline3 = useRef();
	
	const distanceLabel = useRef();
	const verticalLabel = useRef();
	const horizontalLabel = useRef();
	

	const labelStyle = {
		font: '14px monospace',
		showBackground: true,
		horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
		verticalOrigin: Cesium.VerticalOrigin.CENTER,
		pixelOffset: new Cesium.Cartesian2(0, 0),
		eyeOffset: new Cesium.Cartesian3(0, 0, -50),
		fillColor: Cesium.Color.WHITE,
	};
	
	// used when opening 3d model from the same tab as the main map
	/*const {state} = useLocation();
	const cx = state?.cx;
	const cy = state?.cy;
	const cz = state?.cz;*/
	
	// used when opening 3d model from a new tab
	const location = useLocation();
	const cx = isNaN(parseFloat(location?.search?.split("&")[0]?.split("=")[1])) ? undefined : parseFloat(location?.search?.split("&")[0]?.split("=")[1]);
	const cy = isNaN(parseFloat(location?.search?.split("&")[1]?.split("=")[1])) ? undefined : parseFloat(location?.search?.split("&")[1]?.split("=")[1]);
	const cz = isNaN(parseFloat(location?.search?.split("&")[2]?.split("=")[1])) ? undefined : parseFloat(location?.search?.split("&")[2]?.split("=")[1]);
	// console.log("Cx, cy, cz" , cx, cy, cz);
	const pid = (location?.search?.split("&")[3]?.split("=")[1] !== undefined) ? (location?.search?.split("&")[3]?.split("=")[1]) : undefined;
	// console.log(pid);

	const handleRefresh=()=>{setRefreshToken(prev=>!prev)}

	proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
	proj4.defs(
		"EPSG:3857",
		"+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
	);

	useEffect(()=>{
		const apiInstance = new api();
	
		const url_count = `cesium/projekti/${pid}/config/count`;
		apiInstance.Call(url_count, "get").then(
		  (resp) => {
			// console.log(resp);
			setSourceCount(resp.data.count);
		  }
		)
	}, [])


	useEffect(() => {
		//Cesium.Ion.defaultAccessToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJiMDVhZmY2Yi1mYTRjLTQ1NTYtODJmZC0wOTZlZDE4Yzg1MzEiLCJpZCI6MTE1Nzc0LCJpYXQiOjE2NjkxNTI5NzV9.3yUjYxWXb2KtUMQjH9WWd1DQDVcN-8WnxJeTAO8mmXI";
		Cesium.Ion.defaultAccessToken = "" // ne zelimo koristiti Cesium Ion

		// arrays for storing base layers and layers
		const baseLayerViewModels = []
		const layers = []
	
		// define base layers
		// baseLayerViewModels.push(new Cesium.ProviderViewModel({
		//   name: 'Open\u00adStreet\u00adMap',
		//   iconUrl: Cesium.buildModuleUrl('Widgets/Images/ImageryProviders/openStreetMap.png'),
		//   tooltip: 'OpenStreetMap (OSM) is a collaborative project to create a free editable \
		// 		map of the world.\nhttp://www.openstreetmap.org',
		//   creationFunction: function () {
		//     return new Cesium.OpenStreetMapImageryProvider({
		//       url: 'https://a.tile.openstreetmap.org/'
		//     });
		//   }
		// }));
		// baseLayerViewModels.push(new Cesium.ProviderViewModel({
		//   name: "DGU DOF",
		//   tooltip: "DGU DOF",
		//   iconUrl: "http://geoportal.dgu.hr/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng8&TRANSPARENT=true&LAYERS=DOF&TILED=true&WIDTH=256&HEIGHT=256&CRS=EPSG%3A3857&STYLES=&BBOX=2015491.5618235283%2C5257033.057341281%2C2016103.0580498097%2C5257644.553567563",
		//   creationFunction: () => {
		//     return new Cesium.WebMapServiceImageryProvider({
		//       url: "http://geoportal.dgu.hr/wms?",
		//       layers: "DOF",
		//       crs: "EPSG:3857",
		//       parameters: {
		//         format: "image/png",
		//         transparent: true
		//       }
		//     })
		//   }
		// }))
	
		// Viewer
		if (!viewer.current) {
			viewer.current = new Cesium.Viewer('cesiumContainer', {
				animation: false,
				baseLayerPicker: true, // Enable the base layer picker
				geocoder: false,
				maximumRenderTimeChange: Infinity,
				requestRenderMode: true,
				scene3DOnly: true,
				timeline: false,
				infoBox: false,
				selectionIndicator: false,
				globe: new Cesium.Globe(Cesium.Ellipsoid.WGS84),
				terrainProviderViewModels: [],
				
			});

			// Define the DGU DOF base layer view model
			const dguDofViewModel = new Cesium.ProviderViewModel({
				name: "DGU DOF",
				tooltip: "DGU DOF",
				iconUrl: "https://geoportal.dgu.hr/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng8&TRANSPARENT=true&LAYERS=DOF&TILED=true&WIDTH=256&HEIGHT=256&CRS=EPSG%3A3857&STYLES=&BBOX=2015491.5618235283%2C5257033.057341281%2C2016103.0580498097%2C5257644.553567563",
				creationFunction: () => {
					return new Cesium.WebMapServiceImageryProvider({
						url: "https://geoportal.dgu.hr/wms?",
						layers: "DOF",
						crs: "EPSG:3857",
						parameters: {
							format: "image/png",
							transparent: true
						}
					})
				}
			});

			// Define the OpenStreetMap base layer view model
			const osmViewModel = new Cesium.ProviderViewModel({
				name: 'OpenStreetMap',
				iconUrl: Cesium.buildModuleUrl('Widgets/Images/ImageryProviders/openStreetMap.png'),
				tooltip: 'OpenStreetMap (OSM) is a collaborative project to create a free editable \
					map of the world.\nhttp://www.openstreetmap.org',
				creationFunction: function () {
					return new Cesium.OpenStreetMapImageryProvider({
						url: 'https://a.tile.openstreetmap.org/'
					});
				}
			});

			// Set the imageryProviderViewModels property to an array containing only the two base layers you want to keep
			viewer.current.baseLayerPicker.viewModel.imageryProviderViewModels = [dguDofViewModel, osmViewModel];
			viewer.current.baseLayerPicker.viewModel.selectedImagery = osmViewModel;

		}

	
		camera.current = viewer.current.camera;
		scene.current = viewer.current.scene;
		points.current = scene.current.primitives.add(new Cesium.PointPrimitiveCollection());
		polylines.current = scene.current.primitives.add(new Cesium.PolylineCollection());
	
		setHandler( new Cesium.ScreenSpaceEventHandler(scene.current.canvas));
	
		// var ellipsoid = Cesium.Ellipsoid.WGS84;
		// var geodesic = new Cesium.EllipsoidGeodesic();
	
		// define layers
		// 	layers.push(new Cesium.ImageryLayer(new Cesium.WebMapServiceImageryProvider({
		//   url: gs_url,
		//   layers: "ENA:105_DOF",
		//   crs: "EPSG:3857",
		//   parameters: {
		//     format: "image/vnd.jpeg-png",
		//     transparent: true,
		//   }
		// })))
		// layers.push(new Cesium.ImageryLayer(new Cesium.WebMapServiceImageryProvider({
		//     url: gs_url,
		//     layers: "ENA:105_DKP",
		//     crs: "EPSG:3857",
		//     parameters: {
		//       format: "image/vnd.jpeg-png",
		//       transparent: true,
		//     }
		// })))
		// layers.push(new Cesium.ImageryLayer(new Cesium.WebMapServiceImageryProvider({
		//     url: gs_url,
		//     layers: "ENA:105_KB",
		//     crs: "EPSG:3857",
		//     parameters: {
		//       format: "image/vnd.jpeg-png",
		//       transparent: true,
		//     }
		// })))
	
		// add layers to viewer
		//layers.forEach((layer) => viewer.current.imageryLayers.add(layer))
	
		// Prehnit d.o.o. logo
		scene.current.frameState.creditDisplay.addDefaultCredit(new Cesium.Credit('<a href="https://prehnit.hr"><img src="logo.png"/></a>'));
	
		// Cesium tileset
		
		var tilesets = [];
		
		for(let i = 0; i < sourceCount; i++ ){
	 	  
		  const authorizationHeader = authHeader();
			
		  const url = apiPath + "cesium/projekti/" + pid + "/" + i + "/config";
	  
		  const resource = new Cesium.Resource({
			url: url,
			headers: authorizationHeader
		  });
	
		  let new_set = new Cesium.Cesium3DTileset({
			//debugShowBoundingVolume: true,
			//debugShowGeometricError: true,
			//debugShowRenderingStatistics: true,
			dynamicScreenSpaceError: true,
			immediatelyLoadDesiredLevelOfDetail: true,
			maximumScreenSpaceError: 1,
			skipLevelOfDetail: true,
			url: resource
		  });
	
		  tilesets.push(new_set)
		}
	  
		// console.log("Tilesets", tilesets)
	
		tileset_list.current = tilesets;
	
		// tileset.current.allTilesLoaded.addEventListener(function () {
		//   console.log('All tiles are loaded');
		// });
	
		// Konfiguriraj što radi home gumb1207
		viewer.current.homeButton.viewModel.command.beforeExecute.addEventListener(function (commandInfo) {
		  viewer.current.flyTo(tileset_list.current[0]);
		  commandInfo.cancel = true;
		});
	
		// Add tileset to viewer and set initial camera position
		scene.current.screenSpaceCameraController.enableCollisionDetection = true;
	
	   
		tilesets.forEach((tileset) => {
		  tileset.readyPromise
			.then(() => {
			  const boundingSphere = tileset.boundingSphere;
			  camera.current.viewBoundingSphere(
				boundingSphere,
				new Cesium.HeadingPitchRange(
				  0.5,
				  -0.2,
				  boundingSphere.radius * 4.0
				)
			  );
			  camera.current.lookAtTransform(Cesium.Matrix4.IDENTITY);
			})
			.catch((error) => {
			  throw error;
			});
	  
		  viewer.current.scene.primitives.add(tileset);
		});
	
		//scene.current.primitives.add(tileset.current);
		viewer.current.zoomTo(tilesets);
	
		// setLayers(layers);
		// setBaseLayers(baseLayerViewModels);
	
		// return () => {
		//   if (viewer.current) {
		//     viewer.current.destroy();
		//     viewer.current = null;
		//   }
		// };
	  }, [sourceCount]);

	  /*
	  async function loadTiles() {
		try {
			const tileset = await Cesium3DTileset.fromUrl(resource);
			tileset.current = tileset;
			tileset.current = new Cesium.Cesium3DTileset({
				//debugShowBoundingVolume: true,
				//debugShowGeometricError: true,
				//debugShowRenderingStatistics: true,
				dynamicScreenSpaceError: true,
				immediatelyLoadDesiredLevelOfDetail: true,
				maximumScreenSpaceError: 1,
				skipLevelOfDetail: true,
				url: resource
			});
			const boundingSphere = tileset.current.boundingSphere;
			camera.current.viewBoundingSphere(boundingSphere, new Cesium.HeadingPitchRange(0.5, -0.2, boundingSphere.radius * 4.0));
			camera.current.lookAtTransform(Cesium.Matrix4.IDENTITY);
		} catch(error) {
			console.log(`There was an error while creating the 3D tileset. ${error}`);
		}
	  }
	  */

	  useEffect(() => {
		if (viewer.current !== undefined && viewer.current.camera) {
		  
		  const flyToHome = cx==undefined || cy==undefined || cz == undefined;
		  
		  //If object cordinates are not set fly to entire tileset
		  if(flyToHome){
			setTimeout(() => {
			  //console.log("Fly you fool")
			  viewer.current.flyTo(tileset.current)
			}, 2000);
			return;
		  } 
		  //Else take and fly to given cordinates
		  const flyOptions = {};
		  const heightOffset = 50;
		  const longitude = cx;
		  const laditude = cy;
		  const height = cz + heightOffset;
	
		  const posCartographic = new Cesium.Cartographic(longitude, laditude, height);
		  const posCartesian = Cesium.Cartographic.toCartesian(posCartographic);
		  flyOptions.destination = posCartesian;
		  
		  setTimeout(() => {
			//console.log("Fly you fool")
			camera.current.flyTo(flyOptions);
		  }, 2000);
		}
	  }, [viewer.current])
	
	  useEffect(()=>{
	
		// Mouse over the globe to see the cartographic position
		
		handler?.setInputAction(function (click) {
		  // console.log(click);
		  if(!drawAllowed) {
			// console.log("Drawing not allowed!");
			return;
		  }
		  setDrawActive(true);
		  if (scene.current.mode !== Cesium.SceneMode.MORPHING) {
			const pickedObject = scene.current.pick(click.position);
			if (scene.current.pickPositionSupported && Cesium.defined(pickedObject)) {
	
			  const cartesian = scene.current.pickPosition(click.position);
			  // console.log(cartesian);
			  if (Cesium.defined(cartesian)) {
				if (points.current.length === 2) {
				  points.current.removeAll();
				  polylines.current.removeAll();
				  viewer.current.entities.remove(distanceLabel.current);
				  viewer.current.entities.remove(horizontalLabel.current);
				  viewer.current.entities.remove(verticalLabel.current);
				}
				//add first point
				if (points.current.length === 0) {
				  point1.current = points.current.add({
					position: new Cesium.Cartesian3(cartesian.x, cartesian.y, cartesian.z),
					color: LINEPOINTCOLOR
				  });
				} //add second point and lines
				else if (points.current.length === 1) {
				  point2.current = points.current.add({
					position: new Cesium.Cartesian3(cartesian.x, cartesian.y, cartesian.z),
					color: LINEPOINTCOLOR
				  });
				  point1GeoPosition.current = Cesium.Cartographic.fromCartesian(point1.current.position);
				  point2GeoPosition.current = Cesium.Cartographic.fromCartesian(point2.current.position);
				  point3GeoPosition.current = Cesium.Cartographic.fromCartesian(new Cesium.Cartesian3(point2.current.position.x, point2.current.position.y, point1.current.position.z));
	
				  const pl1Positions = [
					new Cesium.Cartesian3.fromRadians(point1GeoPosition.current.longitude, point1GeoPosition.current.latitude, point1GeoPosition.current.height),
					new Cesium.Cartesian3.fromRadians(point2GeoPosition.current.longitude, point2GeoPosition.current.latitude, point2GeoPosition.current.height)
				  ];
				  const pl2Positions = [
					new Cesium.Cartesian3.fromRadians(point2GeoPosition.current.longitude, point2GeoPosition.current.latitude, point2GeoPosition.current.height),
					new Cesium.Cartesian3.fromRadians(point2GeoPosition.current.longitude, point2GeoPosition.current.latitude, point1GeoPosition.current.height)
				  ];
				  const pl3Positions = [
					new Cesium.Cartesian3.fromRadians(point1GeoPosition.current.longitude, point1GeoPosition.current.latitude, point1GeoPosition.current.height),
					new Cesium.Cartesian3.fromRadians(point2GeoPosition.current.longitude, point2GeoPosition.current.latitude, point1GeoPosition.current.height)
				  ];
	
				  polyline1.current = polylines.current.add({
					show: true,
					positions: pl1Positions,
					width: 1,
					material: new Cesium.Material({
					  fabric: {
						type: 'Color',
						uniforms: {
						  color: LINEPOINTCOLOR
						}
					  }
					})
				  });
				  polyline2.current = polylines.current.add({
					show: true,
					positions: pl2Positions,
					width: 1,
					material: new Cesium.Material({
					  fabric: {
						type: 'PolylineDash',
						uniforms: {
						  color: LINEPOINTCOLOR,
						}
					  },
					})
				  });
				  polyline3.current = polylines.current.add({
					show: true,
					positions: pl3Positions,
					width: 1,
					material: new Cesium.Material({
					  fabric: {
						type: 'PolylineDash',
						uniforms: {
						  color: LINEPOINTCOLOR,
						}
					  },
					})
				  });
				  let labelZ;
				  if (point2GeoPosition.current.height >= point1GeoPosition.current.height) {
					labelZ = point1GeoPosition.current.height + (point2GeoPosition.current.height - point1GeoPosition.current.height) / 2.0;
				  } else {
					labelZ = point2GeoPosition.current.height + (point1GeoPosition.current.height - point2GeoPosition.current.height) / 2.0;
				  };
	
				  addDistanceLabel(point1.current, point2.current, labelZ);
	
				}
			  }
			}
		  }
		}, Cesium.ScreenSpaceEventType.LEFT_CLICK);
	  }, [drawAllowed])
	
	
	  const addDistanceLabel = (p1, p2, height) => {
		p1.cartographic = ellipsoid.current.cartesianToCartographic(p1.position);
		p2.cartographic = ellipsoid.current.cartesianToCartographic(p2.position);
		p1.longitude = parseFloat(Cesium.Math.toDegrees(p1.position.x));
		p1.latitude = parseFloat(Cesium.Math.toDegrees(p1.position.y));
		p2.longitude = parseFloat(Cesium.Math.toDegrees(p2.position.x));
		p2.latitude = parseFloat(Cesium.Math.toDegrees(p2.position.y));
	
		const l1 = { ...labelStyle };
		l1.text = getHorizontalDistanceString(p1, p2);
		horizontalLabel.current = viewer.current.entities.add({
		  position: getMidpoint(p1, p2, point1GeoPosition.current.height),
		  label: l1
		});
	
		const l2 = { ...labelStyle }
		l2.text = getDistanceString(p1, p2);
		distanceLabel.current = viewer.current.entities.add({
		  position: getMidpoint(p1, p2, height),
		  label: l2
		});
	
		const l3 = { ...labelStyle }
		l3.text = getVerticalDistanceString();
		verticalLabel.current = viewer.current.entities.add({
		  position: getMidpoint(p2, p2, height),
		  label: l3
		});
	  };
	
	  const getHorizontalDistanceString = (p1, p2) => {
		geodesic.current.setEndPoints(p1.cartographic, p2.cartographic);
		var meters = geodesic.current.surfaceDistance.toFixed(2);
		if (meters >= 1000) {
		  return (meters / 1000).toFixed(1) + ' км ⟷';
		}
		return meters + ' м ⟷';
	  };
	
	  const getVerticalDistanceString = () => {
		const heights = [point1GeoPosition.current.height, point2GeoPosition.current.height];
		const meters = Math.max.apply(Math, heights) - Math.min.apply(Math, heights);
		if (meters >= 1000) {
		  return (meters / 1000).toFixed(1) + ' км ↥';
		}
		return meters.toFixed(2) + ' м ↥';
	  };
	
	  const getDistanceString = (p1, p2) => {
		geodesic.current.setEndPoints(p1.cartographic, p2.cartographic);
		const horizontalMeters = geodesic.current.surfaceDistance.toFixed(2);
		const heights = [point1GeoPosition.current.height, point2GeoPosition.current.height];
		const verticalMeters = Math.max.apply(Math, heights) - Math.min.apply(Math, heights);
		const meters = Math.pow((Math.pow(horizontalMeters, 2) + Math.pow(verticalMeters, 2)), 0.5);
	
		if (meters >= 1000) {
		  return (meters / 1000).toFixed(1) + ' км';
		}
		return meters.toFixed(2) + ' м';
	  };
	
	  const getMidpoint = (p1, p2, height) => {
		const scratch = new Cesium.Cartographic();
		geodesic.current.setEndPoints(p1.cartographic, p2.cartographic);
		const midpointCartographic = geodesic.current.interpolateUsingFraction(0.5, scratch);
		return Cesium.Cartesian3.fromRadians(midpointCartographic.longitude, midpointCartographic.latitude, height);
	  };
	
	  const getTriangleMidpoint = (p1, p2, height) => {
		const scratch = new Cesium.Cartographic();
		geodesic.current.setEndPoints(p1.cartographic, p2.cartographic);
		const midpointCartographic = geodesic.current.interpolateUsingFraction(0.5, scratch);
		return Cesium.Cartesian3.fromRadians(midpointCartographic.longitude, midpointCartographic.latitude, height);
	  };
	
	  const handleResetDraw = ()=>{
		points.current.removeAll();
		polylines.current.removeAll();
		viewer.current.entities.remove(distanceLabel.current);
		viewer.current.entities.remove(horizontalLabel.current);
		viewer.current.entities.remove(verticalLabel.current);
		setDrawActive(false);
	  }
	
	  return (
		<>
		  <div id='cesium-draw-toolbar' 
			style={
			  {
				display: "flex",
				flexDirection: "column",
				gap: 2,
				position: "absolute",
				right: "1%",
				top: "25%",
				height: "50%",
				justifyContent: "center",
				alignItems: "end"
			  }
			}
		  >
			<Tooltip title={drawAllowed ? t("buttons.toggle_drawing_off") : t("buttons.toggle_drawing_on")} placement='left'>
			  <DesignServicesOutlinedIcon  
				fontSize='large' 
				cursor="pointer"
				color={drawAllowed ? '' : 'disabled'} 
				sx={
				  {
					backgroundColor: theme.palette.background.default,
					borderRadius: "10%",
					zIndex: 999,
	
					":hover":{
					  backgroundColor: theme.palette.secondary,
					  border: "1px solid white"
					}
				  }
				}
				onClick={()=>{ 
				  if(drawActive) handleResetDraw();
				  if(drawAllowed) setDrawAllowed(false);
				  else setDrawAllowed(true);
				}
				}
			  />
			</Tooltip>
			<Tooltip title={t("buttons.delete_drawings")} placement='left'>
			  <DeleteOutlineOutlinedIcon 
	
				fontSize='large' 
				color={drawActive ? '' : 'disabled'} 
				cursor={drawActive ? 'pointer': 'not-allowed'}
				
				sx={
				  {
					backgroundColor: theme.palette.background.default,
					borderRadius: "10%",
					zIndex: 999,
					
					":hover":{
					  backgroundColor: theme.palette.secondary,
					  border: "1px solid white"
					}
				  }
				}
				onClick={()=>{
				  if(!drawActive) return;
				  handleResetDraw();
				  handleRefresh();
				}}
			  />
			</Tooltip>
		  </div>
		  <div id="cesiumContainer">
		  </div>
		</>
	  );
	}
	
	export default CesiumPage;